// Client/src/RiskHeatmap.jsx
import React from "react";
import { getReportLabels } from "./reportI18n";

/**
 * Publication-oriented 5x5 risk heatmap.
 *
 * Design principle (TVAC report v4.2):
 * - the matrix is a prioritization aid, not an infographic or probability model;
 * - typography and whitespace carry the page;
 * - colour is semantic and restrained;
 * - the risk register remains readable as report prose beside / below the matrix.
 */

function normalizeLevel(value) {
  if (value == null) return null;

  if (typeof value === "number") {
    const n = Math.round(value);
    if (n < 1 || n > 5) return null;
    return n;
  }

  const s = String(value).toLowerCase();
  if (s.includes("very high") || s.includes("critical") || s.includes("severe")) return 5;
  if (s.includes("high")) return 4;
  if (s.includes("medium") || s.includes("mid") || s.includes("significant")) return 3;
  if (s.includes("low") || s.includes("moderate")) return 2;
  return null;
}

function severityBand(impact, likelihood) {
  const total = Number(impact || 0) + Number(likelihood || 0);
  if (total >= 9) return "critical";
  if (total >= 7) return "high";
  if (total >= 5) return "medium";
  return "low";
}

function getHeatmapCopy(reportLanguageCode = "en") {
  const copy = {
    en: {
      intro:
        "This matrix prioritizes the report’s identified risks by impact and likelihood. It is not a measured probability model; each position should be read together with the case-specific description and mitigation.",
      registerTitle: "Priority risks",
      howToRead: "How to read this page",
      interpretation:
        "Higher placement indicates greater impact; movement to the right indicates greater likelihood. Risks that share a cell can still have different causes and require different responses.",
      scaleNote: "Risk zones are indicative prioritization bands, not measured probabilities.",
    },
    da: {
      intro:
        "Denne matrix prioriterer rapportens identificerede risici efter konsekvens og sandsynlighed. Den er ikke en målt sandsynlighedsmodel; hver placering skal læses sammen med den case-specifikke beskrivelse og afbødning.",
      registerTitle: "Prioriterede risici",
      howToRead: "Sådan læses siden",
      interpretation:
        "En højere placering angiver større konsekvens; bevægelse mod højre angiver større sandsynlighed. Risici i samme felt kan stadig have forskellige årsager og kræve forskellige handlinger.",
      scaleNote: "Risikozonerne er vejledende prioriteringsbånd, ikke målte sandsynligheder.",
    },
    de: {
      intro:
        "Diese Matrix priorisiert die im Bericht identifizierten Risiken nach Auswirkung und Wahrscheinlichkeit. Sie ist kein gemessenes Wahrscheinlichkeitsmodell; jede Position ist zusammen mit der fallspezifischen Beschreibung und Minderung zu lesen.",
      registerTitle: "Priorisierte Risiken",
      howToRead: "So ist diese Seite zu lesen",
      interpretation:
        "Eine höhere Position bedeutet eine größere Auswirkung; eine Bewegung nach rechts bedeutet eine höhere Wahrscheinlichkeit. Risiken im selben Feld können unterschiedliche Ursachen haben und unterschiedliche Maßnahmen erfordern.",
      scaleNote: "Die Risikozonen sind indikative Priorisierungsbänder und keine gemessenen Wahrscheinlichkeiten.",
    },
    fr: {
      intro:
        "Cette matrice hiérarchise les risques identifiés dans le rapport selon leur impact et leur probabilité. Il ne s’agit pas d’un modèle probabiliste mesuré; chaque position doit être lue avec la description et l’atténuation propres au cas.",
      registerTitle: "Risques prioritaires",
      howToRead: "Comment lire cette page",
      interpretation:
        "Une position plus élevée indique un impact plus important; un déplacement vers la droite indique une probabilité plus élevée. Des risques situés dans la même cellule peuvent néanmoins avoir des causes et des réponses différentes.",
      scaleNote: "Les zones de risque sont des bandes indicatives de priorisation, et non des probabilités mesurées.",
    },
    es: {
      intro:
        "Esta matriz prioriza los riesgos identificados en el informe según impacto y probabilidad. No es un modelo de probabilidad medido; cada posición debe leerse junto con la descripción y mitigación específicas del caso.",
      registerTitle: "Riesgos prioritarios",
      howToRead: "Cómo leer esta página",
      interpretation:
        "Una posición más alta indica mayor impacto; el desplazamiento hacia la derecha indica mayor probabilidad. Los riesgos que comparten celda pueden tener causas distintas y requerir respuestas diferentes.",
      scaleNote: "Las zonas de riesgo son bandas indicativas de priorización, no probabilidades medidas.",
    },
    "zh-Hans": {
      intro:
        "该矩阵按影响程度和发生可能性对报告中识别出的风险进行优先排序。它并非经过测量的概率模型；每个位置都应结合本案例的风险说明和缓释措施理解。",
      registerTitle: "优先风险",
      howToRead: "如何阅读本页",
      interpretation:
        "位置越高表示影响越大；向右移动表示发生可能性越高。位于同一单元格的风险仍可能具有不同成因，并需要不同应对措施。",
      scaleNote: "风险区域仅为优先级指示区间，并非经过测量的概率。",
    },
    "pt-BR": {
      intro:
        "Esta matriz prioriza os riscos identificados no relatório por impacto e probabilidade. Ela não é um modelo de probabilidade mensurado; cada posição deve ser lida em conjunto com a descrição e a mitigação específicas do caso.",
      registerTitle: "Riscos prioritários",
      howToRead: "Como ler esta página",
      interpretation:
        "Uma posição mais alta indica maior impacto; o deslocamento para a direita indica maior probabilidade. Riscos na mesma célula ainda podem ter causas diferentes e exigir respostas diferentes.",
      scaleNote: "As zonas de risco são faixas indicativas de priorização, não probabilidades mensuradas.",
    },
  };
  return copy[reportLanguageCode] || copy.en;
}

export default function RiskHeatmap({ result, reportLanguageCode = "en" }) {
  if (!result) return null;

  const labels = getReportLabels(reportLanguageCode);
  const copy = getHeatmapCopy(reportLanguageCode);
  const deep =
    result.deep_assessment ||
    result.deepAssessment ||
    result.deep_assessment_v2 ||
    {};

  const rawRegister = Array.isArray(deep.risk_register) ? deep.risk_register : [];

  const risks = rawRegister
    .map((r) => {
      const impact = normalizeLevel(r.impact);
      const likelihood = normalizeLevel(r.likelihood);
      if (!impact || !likelihood) return null;
      return {
        label: r.risk || labels.notSpecified || "Unlabeled risk",
        mitigation: r.mitigation || "",
        impact,
        likelihood,
      };
    })
    .filter(Boolean)
    .map((r, idx) => ({ ...r, ref: `R${idx + 1}` }));

  if (!risks.length) return null;

  const grid = {};
  for (let impact = 1; impact <= 5; impact += 1) {
    for (let likelihood = 1; likelihood <= 5; likelihood += 1) {
      grid[`${impact}-${likelihood}`] = [];
    }
  }
  risks.forEach((risk) => {
    grid[`${risk.impact}-${risk.likelihood}`].push(risk);
  });

  const impactLabels = {
    5: labels.high || "High",
    4: labels.medHigh || "Medium-high",
    3: labels.medium || "Medium",
    2: labels.medLow || "Medium-low",
    1: labels.low || "Low",
  };

  const likelihoodLabels = {
    1: labels.rare || "Rare",
    2: labels.unlikely || "Unlikely",
    3: labels.possible || "Possible",
    4: labels.likely || "Likely",
    5: labels.almostCertain || "Almost certain",
  };

  const mitigationLabel =
    reportLanguageCode === "da" ? "Afbødning" :
    reportLanguageCode === "de" ? "Minderung" :
    reportLanguageCode === "fr" ? "Atténuation" :
    reportLanguageCode === "es" ? "Mitigación" :
    reportLanguageCode === "zh-Hans" ? "缓释措施" :
    reportLanguageCode === "pt-BR" ? "Mitigação" :
    "Mitigation";

  return (
    <div className="card risk-heatmap-card publication-risk-heatmap-card">
      <div className="publication-chapter-heading publication-chapter-heading--major">
        <span className="publication-chapter-number">06</span>
        <span className="publication-chapter-divider" aria-hidden="true" />
        <h3>{labels.riskHeatmap}</h3>
      </div>

      <p className="publication-lead risk-heatmap-intro">{copy.intro}</p>

      <div className="risk-heatmap-publication-layout">
        <div className="risk-heatmap-matrix-panel">
          <div className="risk-heatmap-axis-title risk-heatmap-axis-title--impact">
            <span>{labels.impactAxis || "Impact"}</span><span aria-hidden="true"> ↑</span>
          </div>

          <div className="risk-heatmap-grid-publication">
            <div className="risk-heatmap-corner" />
            {Array.from({ length: 5 }, (_, i) => {
              const likelihood = i + 1;
              return (
                <div key={`lh-${likelihood}`} className="risk-heatmap-col-label">
                  <span>{likelihood}</span>
                  <small>{likelihoodLabels[likelihood]}</small>
                </div>
              );
            })}

            {Array.from({ length: 5 }, (_, rowIndex) => {
              const impact = 5 - rowIndex;
              return (
                <React.Fragment key={`impact-${impact}`}>
                  <div className="risk-heatmap-row-label">
                    <span>{impact}</span>
                    <small>{impactLabels[impact]}</small>
                  </div>

                  {Array.from({ length: 5 }, (_, colIndex) => {
                    const likelihood = colIndex + 1;
                    const cellRisks = grid[`${impact}-${likelihood}`] || [];
                    return (
                      <div
                        key={`${impact}-${likelihood}`}
                        className={`risk-heatmap-cell risk-heatmap-cell--${severityBand(impact, likelihood)}`}
                      >
                        {cellRisks.length > 0 && (
                          <div className={`risk-heatmap-marker ${cellRisks.length > 1 ? "risk-heatmap-marker--cluster" : ""}`}>
                            {cellRisks.map((risk) => (
                              <span key={risk.ref}>{risk.ref}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </div>

          <div className="risk-heatmap-axis-title risk-heatmap-axis-title--likelihood">
            <span>{labels.likelihoodAxis || "Likelihood"}</span><span aria-hidden="true"> →</span>
          </div>
          <p className="risk-heatmap-scale-note">{copy.scaleNote}</p>
        </div>

        <div className="risk-heatmap-register" aria-label={copy.registerTitle}>
          <h4>{copy.registerTitle}</h4>
          {risks.map((risk) => (
            <div key={risk.ref} className="risk-heatmap-register-item">
              <div className="risk-heatmap-register-heading">
                <span className="risk-heatmap-ref">{risk.ref}</span>
                <strong>{risk.label}</strong>
              </div>
              <div className="risk-heatmap-register-position">
                {labels.impactAxis || "Impact"} {risk.impact} · {labels.likelihoodAxis || "Likelihood"} {risk.likelihood}
              </div>
              {risk.mitigation && (
                <p>
                  <strong>{mitigationLabel}:</strong> {risk.mitigation}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}
